import { useRef, useState } from 'react';

import { Field, LogoUpload, Modal, TextInput } from '@affdice/portal-kit';

import type { PlatformApi } from '../../client.ts';
import { t } from '../../messages.ts';
import { brandLogoActions, brandLogoSrc } from './brand-logo-model.ts';

/**
 * A brand's logo, set or removed by platform staff. It is a direct edit of
 * the operator's data, so like every other one it asks why, and the operator
 * is told (3.9d). The brand's own team sets the same logo from Admin's brand
 * profile without one.
 */
export function SetBrandLogoModal({
  api,
  brand,
  onClose,
  onChanged,
}: {
  api: PlatformApi;
  brand: { brand_id: string; display_name: string; theme_accent: string | null; logo_url?: string | null | undefined };
  onClose: () => void;
  /** The saved path, or null once removed. */
  onChanged: (logoUrl: string | null) => void;
}) {
  const [reason, setReason] = useState('');
  // Read when a button is pressed, so the actions always send what is typed.
  const latest = useRef(reason);
  latest.current = reason;
  const actions = brandLogoActions(api, brand.brand_id, () => latest.current, onChanged);
  return (
    <Modal title={t('brand.logo.title', { name: brand.display_name })} onClose={onClose}>
      <div className="form">
        <Field label={t('edit.reason')} hint={t('edit.reasonHint')}>
          <TextInput value={reason} onChange={setReason} placeholder={t('edit.reasonPlaceholder')} />
        </Field>
        <LogoUpload
          brandName={brand.display_name}
          accent={brand.theme_accent}
          currentSrc={brandLogoSrc(api, brand.logo_url)}
          onSave={actions.save}
          onRemove={actions.remove}
          // The server refuses a direct edit without a reason.
          disabled={reason.trim() === ''}
        />
      </div>
    </Modal>
  );
}
